import React from "react";
import Vizualisation from "../../../../components/Vizualisation";
import useVizualisation from "../../../../helpers/useVizualisation";
import linkData from "../../data/tradeLinks.json";
import { scalePoint, scaleSqrt } from "d3-scale";
import { Text } from "@visx/text";

const BubbleMatrix = ({
  data,
  title,
  note,
  source,
  show,
  USA,
  CAN,
  PHL,
  AUS,
  ARE,
  TWN,
  HKG,
  EU,
  IND,
  CHE,
  ISR,
  SGP,
  NOR,
  UKR,
  KOR,
  JPN,
  MYS,
  BRA,
  CHN,
  RUS,
  other,
  codeUSA,
  codeCAN,
  codePHL,
  codeAUS,
  codeARE,
  codeTWN,
  codeHKG,
  codeEU,
  codeIND,
  codeCHE,
  codeISR,
  codeSGP,
  codeNOR,
  codeUKR,
  codeKOR,
  codeJPN,
  codeMYS,
  codeBRA,
  codeCHN,
  codeRUS,
  codeother,
  importer,
  exporter,
}) => {
  const { ref, width, height, windowSize, inView } = useVizualisation();
  const margin = {
    top: 60,
    right: 30,
    bottom: 20,
    left: width < 700 ? 100 : 140,
  };

  const chartWidth = width - margin.left - margin.right;
  const chartHeight = width < 700 ? windowSize.height - 150 : 600 - 150;

  const highlightMap = {
    open: ["USA"],
    conditional: ["USA", "EUA"],
    limited: ["USA", "EUA", "CHN"],
    eu: ["USA", "EUA"],
    all: ["USA", "EUA", "CHN", "None"],
  };

  const countries = [
    { code: codeUSA, label: USA, country: "USA", frame: "USA" },
    { code: codeCAN, label: CAN, country: "CAN", frame: "USA" },
    { code: codePHL, label: PHL, country: "PHL", frame: "USA" },
    { code: codeAUS, label: AUS, country: "AUS", frame: "USA" },
    { code: codeARE, label: ARE, country: "ARE", frame: "USA" },
    { code: codeTWN, label: TWN, country: "TWN", frame: "USA" },
    { code: codeHKG, label: HKG, country: "HKG", frame: "USA" },
    { code: codeother, label: other, country: "other", frame: "None" },
    { code: codeEU, label: EU, country: "EU", frame: "EUA" },
    { code: codeIND, label: IND, country: "IND", frame: "EUA" },
    { code: codeCHE, label: CHE, country: "CHE", frame: "EUA" },
    { code: codeISR, label: ISR, country: "ISR", frame: "EUA" },
    { code: codeSGP, label: SGP, country: "SGP", frame: "EUA" },
    { code: codeNOR, label: NOR, country: "NOR", frame: "EUA" },
    { code: codeUKR, label: UKR, country: "UKR", frame: "EUA" },
    { code: codeKOR, label: KOR, country: "KOR", frame: "EUA" },
    { code: codeJPN, label: JPN, country: "JPN", frame: "EUA" },
    { code: codeMYS, label: MYS, country: "MYS", frame: "EUA" },
    { code: codeBRA, label: BRA, country: "BRA", frame: "EUA" },
    { code: codeCHN, label: CHN, country: "CHN", frame: "CHN" },
    { code: codeRUS, label: RUS, country: "RUS", frame: "CHN" },
  ];

  const pointScaleHorizontal = scalePoint()
    .domain(countries.map((d) => d.country))
    .range([0, chartWidth]);
  const pointScaleVertical = scalePoint()
    .domain(countries.map((d) => d.country))
    .range([0, chartHeight - margin.top - margin.bottom]);

  const sizeScale = scaleSqrt()
    .domain([0, 18200])
    .range([1, width < 700 ? 25 : 40]);

  const colorScale = {
    USA: "#31989E",
    EUA: "#F2C118",
    CHN: "#481F7C",
    None: "#AAAAAA",
  };

  return (
    <Vizualisation
      title={title}
      note={note}
      source={source}
      ref={ref}
      size="xLarge"
    >
      <svg width={width} height={chartHeight}>
        <g
          className="chart"
          transform={`translate(${margin.left}, ${margin.top})`}
        >
          <g className="grid">
            {countries.map((d) => {
              return (
                <g className="grid" opacity={0.5}>
                  <line
                    x1={0}
                    x2={chartWidth}
                    y1={pointScaleVertical(d.country)}
                    y2={pointScaleVertical(d.country)}
                    stroke={"#eeeeee"}
                    strokeWidth={1}
                  ></line>
                  <line
                    x1={pointScaleHorizontal(d.country)}
                    x2={pointScaleHorizontal(d.country)}
                    y1={0}
                    y2={chartHeight}
                    stroke={"#eeeeee"}
                    strokeWidth={1}
                  ></line>
                </g>
              );
            })}
          </g>
          {linkData.map((d) => {
            return (
              <circle
                cx={pointScaleHorizontal(d.target)}
                cy={pointScaleVertical(d.source)}
                r={
                  highlightMap[show].includes(d.frame) &&
                  !(d.source === "EU" && d.target === "EU")
                    ? sizeScale(d.value)
                    : ["eu", "limited", "all"].includes(show) &&
                      d.source === "EU" &&
                      d.target === "EU"
                    ? sizeScale(d.value)
                    : 0
                }
                fill={colorScale[d.frame]}
                fillOpacity={0.75}
                stroke={colorScale[d.frame]}
                strokeWidth={1.5}
                strokeOpacity={1}
              ></circle>
            );
          })}
          {countries.map((d) => {
            return (
              <g className="axis-labels">
                <Text
                  x={pointScaleHorizontal(d.country)}
                  y={-20}
                  verticalAnchor={width < 700 ? "middle" : "end"}
                  textAnchor={"middle"}
                  fontSize={width < 700 ? "9px" : "10px"}
                  fontWeight={"bold"}
                  fill={colorScale[d.frame]}
                  paintOrder={"stroke"}
                  stroke={"white"}
                  strokeWidth={"2px"}
                  strokeLinecap={"butt"}
                  strokeLinejoin={"miter"}
                  opacity={
                    highlightMap[show].includes(d.frame)
                      ? 1
                      : d.country === "other"
                      ? 1
                      : 0
                  }
                  angle={width < 700 ? -90 : 0}
                >
                  {d.code}
                </Text>
                <Text
                  x={-30}
                  y={pointScaleVertical(d.country)}
                  verticalAnchor={"middle"}
                  textAnchor={"end"}
                  fontSize={"10px"}
                  fontWeight={"bold"}
                  fill={colorScale[d.frame]}
                  paintOrder={"stroke"}
                  stroke={"white"}
                  strokeWidth={"2px"}
                  strokeLinecap={"butt"}
                  strokeLinejoin={"miter"}
                  opacity={
                    highlightMap[show].includes(d.frame)
                      ? 1
                      : d.country === "other"
                      ? 1
                      : 0
                  }
                  width={width < 700 ? 80 : 180}
                >
                  {d.label}
                </Text>
              </g>
            );
          })}
          <Text
            x={width < 700 ? -90 : -130}
            y={chartHeight / 2}
            angle={-90}
            fontSize={"14px"}
          >
            {exporter}
          </Text>
          <Text x={chartWidth / 2} y={-40} fontSize={"14px"}>
            {importer}
          </Text>
        </g>
      </svg>
    </Vizualisation>
  );
};

export default BubbleMatrix;
